import {
  COMMUNITY_WALKTHROUGH_STEPS,
  communityWalkthroughSteps,
} from './steps';
import ReactJoyride, { CallBackProps, STATUS } from 'react-joyride';
import { useWalkthroughContext } from '@/context/walkthrougContext';
import { Tooltip } from '@/components/walkthrough/tooltip';
import { useTranslation } from 'react-i18next';
import { getJoyrideStyles } from '@/components/walkthrough/styles';
import { useHistory } from 'react-router';
import ROUTES from '@/routes/routes';
import { CommunityRouteState } from '../community.types';
import { useCallback, useEffect, useMemo, useRef } from 'react';

/**
 * Pause Joyride (run=false), keep tourActive, set next step.
 * An effect will flip run=true once the step target is in the DOM.
 * Use this for any step that navigates to another tab/page.
 */
export function useCommunityTourAdvance() {
  const { setState } = useWalkthroughContext();

  return useCallback(
    (nextStepIndex: number) => {
      setState({
        run: false,
        tourActive: true,
        stepIndex: nextStepIndex,
      });
    },
    [setState]
  );
}

export const CommunityWalkthrough: React.FC = () => {
  const {
    setState,
    state: { run, stepIndex, tourActive },
  } = useWalkthroughContext();

  const isFinalWalkthroughStep = stepIndex === COMMUNITY_WALKTHROUGH_STEPS.NINE;

  const { t } = useTranslation();
  const history = useHistory();
  const resumeTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearResumeTimer = useCallback(() => {
    if (resumeTimerRef.current) {
      clearInterval(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }
  }, []);

  const endTour = useCallback(() => {
    clearResumeTimer();
    setState({ stepIndex: 0, run: false, tourActive: false });
  }, [clearResumeTimer, setState]);

  // After navigation: tourActive + !run means "waiting for next target"
  useEffect(() => {
    if (!tourActive || run) {
      clearResumeTimer();
      return;
    }

    const target = communityWalkthroughSteps[stepIndex]?.target as
      | string
      | undefined;
    if (!target) {
      return;
    }

    clearResumeTimer();
    let attempts = 0;

    const tryResume = () => {
      if (document.querySelector(target)) {
        clearResumeTimer();
        setState({ run: true, tourActive: true, stepIndex });
        return true;
      }
      return false;
    };

    // Let React commit the new tab/page first
    requestAnimationFrame(() => {
      if (tryResume()) {
        return;
      }
      resumeTimerRef.current = setInterval(() => {
        attempts += 1;
        if (tryResume()) {
          return;
        }
        if (attempts >= 50) {
          clearResumeTimer();
          // Give up this step only if the target never appears
          console.warn(
            `[CommunityWalkthrough] Target not found for step ${stepIndex}: ${target}`
          );
        }
      }, 100);
    });

    return clearResumeTimer;
  }, [clearResumeTimer, run, setState, stepIndex, tourActive]);

  const handleCallback = useCallback(
    (data: CallBackProps) => {
      const { action, index, type, status } = data;

      // Only end when the user finishes the last tip or skips — not when a target
      // is briefly missing during tab navigation.
      if (status === STATUS.SKIPPED) {
        endTour();
        return;
      }
      if (
        status === STATUS.FINISHED &&
        index >= COMMUNITY_WALKTHROUGH_STEPS.NINE
      ) {
        endTour();
        return;
      }

      if (type === 'error:target_not_found') {
        // Resume effect will retry; do not kill the tour.
        return;
      }

      if (type !== 'step:after') {
        return;
      }

      if (action !== 'next' && action !== 'close') {
        return;
      }

      // Interactive spotlight steps: Next is hidden; page handlers advance.
      // Still handle next if it fires (e.g. programmatic).
      const go = (next: number, navigate?: () => void) => {
        setState({ run: false, tourActive: true, stepIndex: next });
        navigate?.();
      };

      if (index === COMMUNITY_WALKTHROUGH_STEPS.ONE) {
        go(index + 1, () =>
          history.push(ROUTES.COMMUNITY.ROOT, {
            activeTabIndex: 1,
          } as CommunityRouteState)
        );
        return;
      }

      if (index === COMMUNITY_WALKTHROUGH_STEPS.TWO) {
        go(index + 1, () =>
          history.push(ROUTES.COMMUNITY.ROOT, {
            activeTabIndex: 0,
          } as CommunityRouteState)
        );
        return;
      }

      if (index === COMMUNITY_WALKTHROUGH_STEPS.THREE) {
        go(index + 1, () => history.push(ROUTES.COMMUNITY.TEAM.POINTS.ROOT));
        return;
      }

      if (index === COMMUNITY_WALKTHROUGH_STEPS.FOUR) {
        go(index + 1, () =>
          history.push(ROUTES.COMMUNITY.ROOT, {
            activeTabIndex: 0,
          } as CommunityRouteState)
        );
        return;
      }

      if (index === COMMUNITY_WALKTHROUGH_STEPS.FIVE) {
        go(index + 1);
        return;
      }

      if (index === COMMUNITY_WALKTHROUGH_STEPS.SIX) {
        go(index + 1, () =>
          history.push(ROUTES.COMMUNITY.ROOT, {
            activeTabIndex: 2,
          } as CommunityRouteState)
        );
        return;
      }

      if (index === COMMUNITY_WALKTHROUGH_STEPS.SEVEN) {
        go(index + 1);
        return;
      }

      if (index === COMMUNITY_WALKTHROUGH_STEPS.EIGHT) {
        go(index + 1, () =>
          history.push(ROUTES.COMMUNITY.ROOT, {
            activeTabIndex: 0,
          } as CommunityRouteState)
        );
        return;
      }

      if (index === COMMUNITY_WALKTHROUGH_STEPS.NINE) {
        endTour();
        return;
      }

      if (index < COMMUNITY_WALKTHROUGH_STEPS.NINE) {
        go(index + 1);
      } else {
        endTour();
      }
    },
    [endTour, history, setState]
  );

  const steps = useMemo(
    () =>
      communityWalkthroughSteps.map((item) => ({
        ...item,
        content: t(item.content as string),
      })),
    [t]
  );

  const joyrideStyles = useMemo(
    () =>
      getJoyrideStyles(
        isFinalWalkthroughStep,
        stepIndex === COMMUNITY_WALKTHROUGH_STEPS.SEVEN ||
          isFinalWalkthroughStep
      ),
    [isFinalWalkthroughStep, stepIndex]
  );

  return (
    <ReactJoyride
      // Remount when resuming a step so Joyride re-binds to the new tab's DOM
      key={run ? `community-tour-run-${stepIndex}` : 'community-tour-idle'}
      steps={steps}
      run={run}
      stepIndex={stepIndex}
      callback={handleCallback}
      continuous
      scrollToFirstStep
      showProgress
      showSkipButton={false}
      disableOverlayClose
      disableCloseOnEsc
      floaterProps={{ disableAnimation: true }}
      tooltipComponent={(props) => (
        <Tooltip
          {...props}
          className={
            stepIndex === COMMUNITY_WALKTHROUGH_STEPS.SEVEN ? 'mt-20' : ''
          }
          pollyNeutralSteps={[...Array(8).keys()]}
          pollyImpressedSteps={[COMMUNITY_WALKTHROUGH_STEPS.NINE]}
          displayCloseButton={props.index < props.size - 1}
        />
      )}
      styles={joyrideStyles}
    />
  );
};
